import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Paper,
  styled,
  Avatar,
  CardHeader,
  useTheme
} from '@mui/material';
import {
  DirectionsCar,
  TwoWheeler,
  AttachFile
} from '@mui/icons-material';

type Vehicle = {
  id: number;
  driverId: number;
  vehicle_no: string;
  vehicle_name: string | null;
  vehicle_type: string;
  registration_certificate: string | null;
  extra_amenities: string | null;
  created_at: string;
  updated_at: string;
};

type VehicleCardProps = {
  vehicles: Vehicle[];
};

// Styled components
const VehicleCardStyled = styled(Card)(({ theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const AmenityChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const VehicleAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicles }) => {
  const theme = useTheme();

  // Function to get appropriate vehicle icon
  const getVehicleIcon = (vehicleType: string) => {
    const type = vehicleType.toLowerCase();
    if (type.includes('car')) return <DirectionsCar />;
    if (type.includes('bike')) return <TwoWheeler />;
    // Default icon
    return <DirectionsCar />;
  };

  // Function to format creation date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Function to parse amenities string
  const parseAmenities = (amenitiesString: string | null) => {
    if (!amenitiesString) return [];
    
    try {
      // Try to parse as JSON if it's in JSON format
      return JSON.parse(amenitiesString);
    } catch (e) {
      // If not JSON, split by commas or semicolons
      return amenitiesString
        .split(/[,;]/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
  };

  if (!vehicles || vehicles.length === 0) {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{ 
          p: 3, 
          textAlign: 'center',
          bgcolor: 'background.default',
          borderStyle: 'dashed'
        }}
      >
        <Typography color="textSecondary">No vehicles registered</Typography>
      </Paper>
    );
  }

  return (
    <Grid container spacing={3}>
      {vehicles.map((vehicle) => (
        <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
          <VehicleCardStyled>
            <CardHeader
              avatar={
                <VehicleAvatar>
                  {getVehicleIcon(vehicle.vehicle_type)}
                </VehicleAvatar>
              }
              title={
                <Typography variant="h6">
                  {vehicle.vehicle_name || `Vehicle ${vehicle.id}`}
                </Typography>
              }
              subheader={vehicle.vehicle_type}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Vehicle Number
                </Typography>
                <Chip 
                  label={vehicle.vehicle_no}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>

              {vehicle.extra_amenities && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Amenities
                  </Typography>
                  <Box display="flex" flexWrap="wrap" mt={1}>
                    {parseAmenities(vehicle.extra_amenities).map((amenity, index) => (
                      <AmenityChip 
                        key={index} 
                        label={amenity} 
                        size="small" 
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {vehicle.registration_certificate && (
                <Box mb={2} display="flex" alignItems="center">
                  <AttachFile fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 0.5 }}>
                    Registration certificate available
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />
              
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Added on
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(vehicle.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Last updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(vehicle.updated_at)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </VehicleCardStyled>
        </Grid>
      ))}
    </Grid>
  );
};

export default VehicleCard;